import { AxiosError } from 'axios'
import { action, computed, flow, makeObservable, observable } from 'mobx'

import { openPdfDocumentFromBlob, requestWrapper } from 'Shared/utils'

import { STORE_UI } from 'Core/constants'
import { CustomGenerator } from 'Core/interfaces'
import {
	entityToLabelValue,
	entityToStringLabelValue,
	getDefaultStoreArrayValue,
	getDefaultStoreValue
} from 'Core/utils'

import {
	getAllOptions,
	getContactDeleteSuccessMessage,
	getContactExistErrorMessage,
	getContactSuccessMessage,
	getHistoricalInvoiceDeleteSuccessMessage,
	getHistoricalInvoiceSuccessMessage,
	getServiceDeleteSuccessMessage,
	getServiceSuccessMessage,
	getSupplierContactDeleteSuccessMessage,
	getSupplierContactSuccessMessage,
	STORE_METADATA
} from 'Portals/Customer/constants'
import { ProgramManagerFilter, ServiceLocation } from 'Portals/Customer/entities'
import { ServiceLocationRepo } from 'Portals/Customer/repositories'

import { RootStore } from '../rootStore/RootStore'

import { ServiceLocationTable } from './ServiceLocationTable'

export class ServiceLocationStore {
	table: ServiceLocationTable
	@observable accessor serviceSite = getDefaultStoreValue<ServiceLocation.Detailed>()
	@observable accessor serviceLocation = getDefaultStoreArrayValue()
	@observable accessor billingLocation = getDefaultStoreArrayValue()
	@observable accessor programManager = getDefaultStoreArrayValue<ProgramManagerFilter>()
	@observable accessor contacts = getDefaultStoreArrayValue<ServiceLocation.Contact.Basic>()
	@observable accessor contact = getDefaultStoreValue<ServiceLocation.Contact.Detailed>()
	@observable accessor services = getDefaultStoreArrayValue<ServiceLocation.Service.Basic>()
	@observable accessor service = getDefaultStoreValue<ServiceLocation.Service.Basic>()
	@observable accessor treatmentMethods = getDefaultStoreArrayValue()
	@observable accessor supplierContacts =
		getDefaultStoreArrayValue<ServiceLocation.SupplierContact.Basic>()
	@observable accessor supplierContact = getDefaultStoreValue<ServiceLocation.SupplierContact.Detailed>()
	@observable accessor historicalInvoices =
		getDefaultStoreArrayValue<ServiceLocation.HistoricalInvoice.Basic>()
	@observable accessor isDocumentDownloading: boolean = false
	@observable accessor error: AxiosError | null = null

	constructor(private rootStore: RootStore) {
		this.table = new ServiceLocationTable(rootStore)
	}

	@computed
	get isFiltersOptionLoading() {
		return (
			this.serviceLocation.isLoading ||
			this.billingLocation.isLoading ||
			this.programManager.isLoading
		)
	}

	@computed
	get serviceLocationOptions() {
		return [getAllOptions(), ...this.serviceLocation.value.map(entityToStringLabelValue)]
	}

	@computed
	get billingLocationOptions() {
		return [getAllOptions(), ...this.billingLocation.value.map(entityToStringLabelValue)]
	}

	@computed
	get programManagerOptions() {
		return [
			getAllOptions(),
			...this.programManager.value.map((entity) => ({
				label: `${entity.lastName}, ${entity.firstName}`,
				value: entity.id.toString()
			}))
		]
	}

	@computed
	get treatmentMethodsOptions() {
		return this.treatmentMethods.value.map(entityToLabelValue)
	}

	@flow.bound
	*retrieveServiceLocation(): CustomGenerator<Entity[]> {
		yield requestWrapper({
			field: this.serviceLocation,
			repository: ServiceLocationRepo.retrieveServiceLocation,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveBillingLocation(): CustomGenerator<Entity[]> {
		yield requestWrapper({
			field: this.billingLocation,
			repository: ServiceLocationRepo.retrieveBillingLocation,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveProgramManager(): CustomGenerator<Entity[]> {
		yield requestWrapper({
			field: this.programManager,
			repository: ServiceLocationRepo.retrieveProgramManager,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveServiceLocationData(id: string) {
		this.error = null
		try {
			yield this.retrieveServiceSite(id)
			this.retrieveContacts(id)
			this.retrieveServices(id)
			this.retrieveTreatmentMethods()
			this.retrieveSupplierContacts(id)
			this.retrieveHistoricalInvoices(id)
			this.rootStore[STORE_METADATA].retrieveUnitsOfMeasure()
		} catch (error) {
			this.error = error as AxiosError
		}
	}

	@flow.bound
	*retrieveServiceSite(id: string): CustomGenerator<ServiceLocation.Detailed> {
		yield requestWrapper({
			field: this.serviceSite,
			repository: ServiceLocationRepo.retrieveServiceSite,
			repositoryParams: id,
			showDefaultError: false
		})
	}

	@flow.bound
	*retrieveContacts(id: string): CustomGenerator<ServiceLocation.Contact.Basic[]> {
		yield requestWrapper({
			field: this.contacts,
			repository: ServiceLocationRepo.retrieveServiceSiteContact,
			repositoryParams: id
		})
	}

	@flow.bound
	*deleteContact(id: number) {
		if (!this.serviceSite.value) {
			return null
		}

		try {
			yield ServiceLocationRepo.deleteContact(this.serviceSite.value.id, id)
			this.rootStore[STORE_UI].showSuccessNotification(getContactDeleteSuccessMessage())
			this.retrieveContacts(this.serviceSite.value.id.toString())
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
	}

	@flow.bound
	*addContact(data: ServiceLocation.Contact.Generic.Request) {
		if (!this.serviceSite.value) {
			return null
		}

		this.rootStore[STORE_UI].setIsRequestSubmitting()

		try {
			yield ServiceLocationRepo.addContact(data, this.serviceSite.value.id)
			this.rootStore[STORE_UI].showSuccessNotification(getContactSuccessMessage())
			this.rootStore[STORE_UI].closeModal()
			this.retrieveContacts(this.serviceSite.value.id.toString())
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
		this.rootStore[STORE_UI].setIsRequestSubmitting(false)
	}

	@flow.bound
	*editContact(data: ServiceLocation.Contact.Generic.Request, contactId: number) {
		if (!this.serviceSite.value) {
			return null
		}

		this.rootStore[STORE_UI].setIsRequestSubmitting()

		try {
			yield ServiceLocationRepo.editContact(data, this.serviceSite.value.id, contactId)
			this.rootStore[STORE_UI].showSuccessNotification(getContactSuccessMessage())
			this.rootStore[STORE_UI].closeModal()
			this.retrieveContacts(this.serviceSite.value.id.toString())
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
		this.rootStore[STORE_UI].setIsRequestSubmitting(false)
	}

	@flow.bound
	*retrieveServiceLocationContactById(
		id: number
	): CustomGenerator<ServiceLocation.Contact.Detailed> {
		yield requestWrapper({
			field: this.contact,
			repository: ServiceLocationRepo.retrieveServiceLocationContactById,
			repositoryParams: { id: this.serviceSite.value?.id || 0, contactId: id }
		})
	}

	@flow.bound
	*retrieveServices(id: string): CustomGenerator<ServiceLocation.Service.Basic[]> {
		yield requestWrapper({
			field: this.services,
			repository: ServiceLocationRepo.retrieveServices,
			repositoryParams: id
		})
	}

	@flow.bound
	*deleteService(id: number) {
		if (!this.serviceSite.value) {
			return null
		}

		try {
			yield ServiceLocationRepo.deleteService(this.serviceSite.value.id, id)
			this.rootStore[STORE_UI].showSuccessNotification(getServiceDeleteSuccessMessage())
			this.retrieveServices(this.serviceSite.value.id.toString())
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
	}

	@flow.bound
	*retrieveServiceLocationServiceById(
		id: number
	): CustomGenerator<ServiceLocation.Service.Basic> {
		yield requestWrapper({
			field: this.service,
			repository: ServiceLocationRepo.retrieveServiceLocationServiceById,
			repositoryParams: { id: this.serviceSite.value?.id || 0, serviceId: id }
		})
	}

	@flow.bound
	*addService(data: ServiceLocation.Service.Request) {
		if (!this.serviceSite.value) {
			return null
		}

		this.rootStore[STORE_UI].setIsRequestSubmitting()

		try {
			yield ServiceLocationRepo.addService(data, this.serviceSite.value.id)
			this.rootStore[STORE_UI].showSuccessNotification(getServiceSuccessMessage())
			this.rootStore[STORE_UI].closeModal()
			this.retrieveServices(this.serviceSite.value.id.toString())
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
		this.rootStore[STORE_UI].setIsRequestSubmitting(false)
	}

	@flow.bound
	*editService(data: ServiceLocation.Service.Request, contactId: Key) {
		if (!this.serviceSite.value) {
			return null
		}

		this.rootStore[STORE_UI].setIsRequestSubmitting()

		try {
			yield ServiceLocationRepo.editService(data, this.serviceSite.value.id, contactId)
			this.rootStore[STORE_UI].showSuccessNotification(getServiceSuccessMessage())
			this.rootStore[STORE_UI].closeModal()
			this.retrieveServices(this.serviceSite.value.id.toString())
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
		this.rootStore[STORE_UI].setIsRequestSubmitting(false)
	}

	@flow.bound
	*checkContactEmail(email: string): CustomGenerator<void, boolean> {
		let result = false
		try {
			yield ServiceLocationRepo.checkContactEmail(email)
			result = true
			this.rootStore[STORE_UI].showErrorNotification(undefined, getContactExistErrorMessage())
		} catch (error) {
			if (error && (error as AxiosError).response?.status !== 404) {
				this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
			}
		}
		return result
	}

	@flow.bound
	*retrieveTreatmentMethods(): CustomGenerator<Entity[]> {
		yield requestWrapper({
			field: this.treatmentMethods,
			repository: ServiceLocationRepo.retrieveTreatmentMethods,
			repositoryParams: null
		})
	}

	@flow.bound
	*retrieveSupplierContacts(id: Key): CustomGenerator<ServiceLocation.SupplierContact.Basic[]> {
		yield requestWrapper({
			field: this.supplierContacts,
			repository: ServiceLocationRepo.retrieveSupplierContacts,
			repositoryParams: id
		})
	}

	@flow.bound
	*retrieveSupplierContact(id: Key): CustomGenerator<ServiceLocation.SupplierContact.Detailed> {
		yield requestWrapper({
			field: this.supplierContact,
			repository: ServiceLocationRepo.retrieveSupplierContact,
			repositoryParams: { id: this.serviceSite.value?.id || 0, contactId: id }
		})
	}

	@flow.bound
	*deleteSupplierContact(id: number) {
		if (!this.serviceSite.value) {
			return null
		}
		this.rootStore[STORE_UI].setIsRequestSubmitting()

		try {
			yield ServiceLocationRepo.deleteSupplierContact(this.serviceSite.value.id, id)
			this.rootStore[STORE_UI].showSuccessNotification(
				getSupplierContactDeleteSuccessMessage()
			)
			this.retrieveSupplierContacts(this.serviceSite.value.id.toString())
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
		this.rootStore[STORE_UI].setIsRequestSubmitting(false)
	}

	@flow.bound
	*addSupplierContact(data: ServiceLocation.SupplierContact.Form) {
		if (!this.serviceSite.value) {
			return null
		}

		this.rootStore[STORE_UI].setIsRequestSubmitting()

		try {
			yield ServiceLocationRepo.addSupplierContact(data, this.serviceSite.value.id)
			this.rootStore[STORE_UI].showSuccessNotification(getSupplierContactSuccessMessage())
			this.rootStore[STORE_UI].closeModal()
			this.retrieveSupplierContacts(this.serviceSite.value.id.toString())
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
		this.rootStore[STORE_UI].setIsRequestSubmitting(false)
	}

	@flow.bound
	*editSupplierContact(data: ServiceLocation.SupplierContact.Form, supplierContactId: number) {
		if (!this.serviceSite.value) {
			return null
		}

		this.rootStore[STORE_UI].setIsRequestSubmitting()

		try {
			yield ServiceLocationRepo.editSupplierContact(
				data,
				this.serviceSite.value.id,
				supplierContactId
			)
			this.rootStore[STORE_UI].showSuccessNotification(getSupplierContactSuccessMessage())
			this.rootStore[STORE_UI].closeModal()
			this.retrieveSupplierContacts(this.serviceSite.value.id.toString())
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}
		this.rootStore[STORE_UI].setIsRequestSubmitting(false)
	}

	@flow.bound
	*retrieveHistoricalInvoices(
		id: Key
	): CustomGenerator<ServiceLocation.HistoricalInvoice.Basic[]> {
		yield requestWrapper({
			field: this.historicalInvoices,
			repository: ServiceLocationRepo.retrieveHistoricalInvoices,
			repositoryParams: id
		})
	}

	@flow.bound
	*deleteHistoricalInvoice(historicalInvoiceId: Key): CustomGenerator<void> {
		const id = this.serviceSite.value?.id

		if (!id || !historicalInvoiceId) {
			return
		}

		this.rootStore[STORE_UI].setIsRequestSubmitting()

		try {
			yield ServiceLocationRepo.deleteHistoricalInvoice(id, historicalInvoiceId)

			this.rootStore[STORE_UI].closeModal()
			this.rootStore[STORE_UI].showSuccessNotification(
				getHistoricalInvoiceDeleteSuccessMessage()
			)
			this.retrieveHistoricalInvoices(id)
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		} finally {
			this.rootStore[STORE_UI].setIsRequestSubmitting(false)
		}
	}

	@flow.bound
	*downloadHistoricalInvoice(
		historicalInvoiceId: Key,
		fileName?: BasicString
	): CustomGenerator<Blob> {
		const id = this.serviceSite.value?.id

		if (!id) {
			return
		}

		this.isDocumentDownloading = true

		try {
			const document = yield ServiceLocationRepo.downloadHistoricalInvoice(
				id,
				historicalInvoiceId
			)

			openPdfDocumentFromBlob(document, fileName)
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		}

		this.isDocumentDownloading = false
	}

	@flow.bound
	*uploadHistoricalInvoice(
		data: ServiceLocation.HistoricalInvoice.Request
	): CustomGenerator<ServiceLocation.HistoricalInvoice.Basic> {
		const id = this.serviceSite.value?.id

		if (!id) {
			return
		}

		this.rootStore[STORE_UI].setIsRequestSubmitting()

		try {
			yield ServiceLocationRepo.uploadHistoricalInvoice(id, data)
			const isMultiple = data.Files.length > 1
			this.rootStore[STORE_UI].showSuccessNotification(
				getHistoricalInvoiceSuccessMessage(isMultiple)
			)
			this.rootStore[STORE_UI].closeModal()
			this.retrieveHistoricalInvoices(id)
		} catch (error) {
			this.rootStore[STORE_UI].showErrorNotification(error as AxiosError)
		} finally {
			this.rootStore[STORE_UI].setIsRequestSubmitting(false)
		}
	}

	@action.bound
	resetServiceLocationContact() {
		this.contact = getDefaultStoreValue()
	}

	@action.bound
	resetServiceLocationService() {
		this.service = getDefaultStoreValue()
	}

	@action.bound
	resetServiceLocationSupplierContact() {
		this.supplierContact = getDefaultStoreValue()
	}
}
